import {
  type CSSProperties,
  type ElementType,
  type MouseEvent,
  type ReactNode,
  type Ref,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { clsx } from 'clsx';
import { match, P } from 'ts-pattern';
import { type Interaction, Interactive, type LinkInteraction } from '../../../util/interaction';
import { HorizontalStack } from '../../alignment/horizontal-stack';
import type { DrawerProps } from '../../dialog/drawer/drawer';
import type { ModalProps } from '../../dialog/modal/modal';
import { useMergedDialogOpenProps } from '../../dialog/utils';
import { BaseLink, type LinkElementType } from '../../link/base-link';
import { buildTypographyToneVariable, type TypographyTone } from '../../typography';
import dropdownStyles from '../../dropdown/dropdown.module.scss';

type RequiredModalProps = Pick<ModalProps, 'trigger' | 'isOpen' | 'onOpenChange'>;
type RequiredDrawerProps = Pick<DrawerProps, 'trigger'>;

// TODO: migrate to interactive
export interface DropdownMenuItem<DialogProps extends RequiredModalProps = any, IDrawerProps extends RequiredDrawerProps = any> {
  dialog?: {
    Component: ElementType<DialogProps>;
    props: Omit<DialogProps, keyof RequiredModalProps>;
  };
  drawer?: {
    Component: ElementType<IDrawerProps>;
    props?: Omit<IDrawerProps, keyof RequiredDrawerProps>;
  };
  disabled?: boolean;
  group?: string;
  icon?: ReactNode;
  id: string;
  interaction?: Exclude<Interaction, LinkInteraction>;
  label: ReactNode;
  LinkElement?: LinkElementType;
  pill?: ReactNode;
  preload?: boolean;
  requiresWrite?: boolean;
  to?: string;
  tone?: TypographyTone;
  onClick?: (e?: MouseEvent) => void;
}

interface DropdownMenuListItemProps extends DropdownMenuItem {
  className?: string;
  ellipsizeOverflow?: boolean;
  handleCloseMenu?: VoidFunction;
  handleCloseDialog?: (subItemId: string) => void;
  handleOpenDialog?: (subItemId: string) => void;
  hasOpenDialog?: boolean;
  ref?: Ref<HTMLButtonElement>;
}

export function DropdownMenuListItem({
  className,
  dialog,
  drawer,
  ellipsizeOverflow,
  handleCloseMenu,
  handleCloseDialog,
  handleOpenDialog,
  hasOpenDialog,
  icon,
  id,
  interaction,
  label,
  LinkElement,
  pill,
  preload,
  to,
  tone = 'md',
  onClick,
  ref,
}: DropdownMenuListItemProps) {
  const labelRef = useRef<HTMLSpanElement>(null);
  const [hasOverflowingLabel, setHasOverflowingLabel] = useState(false);
  const styles = useMemo(
    () =>
      ({
        '--ixf-dropdown-menu-list-item-color': buildTypographyToneVariable(tone),
      }) as CSSProperties,
    [tone],
  );
  const hasDialog = Boolean(dialog || interaction?.type === 'modal');
  const hasDrawer = Boolean(drawer || interaction?.type === 'drawer');
  const providedIsOpen = match({ hasOpenDialog, dialog, drawer, interaction })
    .with({ interaction: { type: 'drawer' } }, (d) => d.interaction.drawer.props?.isOpen)
    .with({ interaction: { type: 'modal' } }, (d) => d.interaction.modal.props?.isOpen)
    .with({ dialog: P.not(P.nullish).select() }, (d) => d.props?.isOpen)
    .with({ drawer: P.not(P.nullish).select() }, (d) => d.props?.isOpen)
    .otherwise(() => undefined);

  const handleBubbleUpDialogOpen = useCallback(
    (nextOpen: boolean) => {
      if (nextOpen) {
        handleOpenDialog?.(id);
      } else {
        handleCloseDialog?.(id);
      }
    },
    [handleCloseDialog, handleOpenDialog, id],
  );

  const providedOnOpenChange = match({ hasOpenDialog, dialog, drawer, interaction })
    .with({ interaction: { type: 'drawer' } }, (d) => d.interaction.drawer.props?.onOpenChange)
    .with({ interaction: { type: 'modal' } }, (d) => d.interaction.modal.props?.onOpenChange)
    .with({ dialog: P.not(P.nullish).select() }, (d) => d.props?.onOpenChange)
    .with({ drawer: P.not(P.nullish).select() }, (d) => d.props?.onOpenChange)
    .otherwise(() => undefined);

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      handleBubbleUpDialogOpen(newOpen);
      providedOnOpenChange?.(newOpen);
    },
    [providedOnOpenChange, handleBubbleUpDialogOpen],
  );
  const [isOpen, onOpenChange] = useMergedDialogOpenProps({ isOpen: providedIsOpen, onOpenChange: handleOpenChange });

  useLayoutEffect(() => {
    const container = labelRef.current;
    if (!container) {
      return;
    }

    function handleResize() {
      if (!container) {
        return;
      }

      setHasOverflowingLabel(container.scrollWidth > container.offsetWidth);
    }

    const observer = new ResizeObserver(handleResize);
    observer.observe(container);
    handleResize();

    return () => {
      observer.disconnect();
    };
  }, []);

  const handleClick = useCallback(
    (e: MouseEvent) => {
      if (onClick) {
        onClick(e);
      }

      if (hasDialog) {
        onOpenChange(true);
      } else if (!hasDrawer) {
        handleCloseMenu?.();
      }
    },
    [handleCloseMenu, hasDialog, hasDrawer, onOpenChange, onClick],
  );

  const mappedInteraction: Interaction | undefined = useMemo(() => {
    if (!interaction) {
      return undefined;
    }

    switch (interaction?.type) {
      case 'modal': {
        if (!interaction.modal) {
          return undefined;
        }

        const { props } = interaction.modal;

        return {
          ...interaction,
          modal: {
            ...interaction.modal,
            props: {
              ...props,
              isOpen,
              onOpenChange,
            },
          },
        };
      }
      case 'drawer': {
        if (!interaction.drawer) {
          return undefined;
        }

        const { props } = interaction.drawer;

        return {
          ...interaction,
          drawer: {
            ...interaction.drawer,
            props: {
              ...props,
              isOpen,
              onOpenChange,
            },
          },
        };
      }
      default:
        return undefined;
    }
  }, [interaction, onOpenChange, isOpen]);

  const baseClassName = clsx(
    dropdownStyles.item,
    dropdownStyles.fullWidth,
    pill && dropdownStyles.hasPill,
    Boolean(icon) && dropdownStyles.hasIcon,
    (to || drawer || dialog || onClick || interaction) && dropdownStyles.isInteractive,
    className,
  );
  const labelElement = label ? (
    <span className={dropdownStyles.itemLabel} ref={labelRef} title={hasOverflowingLabel && typeof label === 'string' ? label : undefined}>
      {label}
    </span>
  ) : null;

  const baseListItemContent = (
    <HorizontalStack align="space-between" blockAlign="center" fullWidth gap={2} wrap="nowrap">
      <HorizontalStack blockAlign="center" className={clsx(ellipsizeOverflow && dropdownStyles.hideOverflow)} gap={2} wrap="nowrap">
        {icon}
        {labelElement}
      </HorizontalStack>
      {pill}
    </HorizontalStack>
  );

  const baseBtn = (
    <li className={clsx(dropdownStyles.paddedItem, dropdownStyles.fullWidth)} style={styles}>
      <button className={baseClassName} onClick={handleClick} ref={ref} type="button">
        {baseListItemContent}
      </button>
    </li>
  );

  if (mappedInteraction) {
    return <Interactive {...mappedInteraction}>{baseBtn}</Interactive>;
  }

  if (drawer?.Component) {
    return <drawer.Component {...drawer.props} isOpen={isOpen} onOpenChange={onOpenChange} trigger={baseBtn} />;
  }

  if (dialog?.Component) {
    return <dialog.Component {...dialog.props} isOpen={isOpen} onOpenChange={onOpenChange} trigger={baseBtn} />;
  }

  if (to) {
    return (
      <li className={clsx(dropdownStyles.paddedItem, dropdownStyles.fullWidth)} style={styles}>
        <BaseLink
          className={baseClassName}
          LinkElement={LinkElement}
          onClick={handleClick}
          preload={preload}
          ref={ref as unknown as Ref<HTMLAnchorElement>}
          to={to}
        >
          {baseListItemContent}
        </BaseLink>
      </li>
    );
  }

  if (onClick) {
    return baseBtn;
  }

  return (
    <li className={clsx(baseClassName, dropdownStyles.paddedItem)} id={id} style={styles}>
      {baseListItemContent}
    </li>
  );
}
